import { deleteApi, getApi, postApi, putApi } from "./helpers/apiMethods";
import type {
  CloneSystemTemplateRequest,
  CreateSummaryTemplateRequest,
  ListSummaryTemplatesResponse,
  SummaryTemplate,
  UpdateSummaryTemplateRequest,
} from "../types/summaryTemplate/SummaryTemplate";

const basePath = (customerId: string) =>
  `/v1/customers/${customerId}/summary-templates`;

export async function listSummaryTemplates(
  customerId: string,
): Promise<ListSummaryTemplatesResponse> {
  const response = await getApi<ListSummaryTemplatesResponse>(basePath(customerId));
  return response.data ?? { templates: [], hiddenSystemTemplateIds: [] };
}

export async function getSummaryTemplate(
  customerId: string,
  templateId: string,
): Promise<SummaryTemplate | null> {
  try {
    const response = await getApi<SummaryTemplate>(`${basePath(customerId)}/${templateId}`);
    return response.data ?? null;
  } catch {
    return null;
  }
}

export async function createSummaryTemplate(
  customerId: string,
  request: CreateSummaryTemplateRequest,
): Promise<SummaryTemplate> {
  const response = await postApi<SummaryTemplate>(basePath(customerId), request);
  if (!response.data) throw new Error("Failed to create summary template.");
  return response.data;
}

export async function updateSummaryTemplate(
  customerId: string,
  templateId: string,
  request: UpdateSummaryTemplateRequest,
): Promise<SummaryTemplate> {
  const response = await putApi<SummaryTemplate>(`${basePath(customerId)}/${templateId}`, request);
  if (!response.data) throw new Error("Failed to update summary template.");
  return response.data;
}

export async function deleteSummaryTemplate(
  customerId: string,
  templateId: string,
): Promise<void> {
  await deleteApi(`${basePath(customerId)}/${templateId}`);
}

export async function setDefaultTemplate(
  customerId: string,
  templateId: string,
): Promise<void> {
  await putApi(`${basePath(customerId)}/default/${templateId}`);
}

export async function hideSystemTemplate(
  customerId: string,
  systemTemplateId: string,
): Promise<void> {
  await putApi(`${basePath(customerId)}/system/${systemTemplateId}/hide`);
}

export async function unhideSystemTemplate(
  customerId: string,
  systemTemplateId: string,
): Promise<void> {
  await putApi(`${basePath(customerId)}/system/${systemTemplateId}/unhide`);
}

export async function cloneSystemTemplate(
  customerId: string,
  systemTemplateId: string,
  request: CloneSystemTemplateRequest,
): Promise<SummaryTemplate> {
  const response = await postApi<SummaryTemplate>(
    `${basePath(customerId)}/system/${systemTemplateId}/clone`,
    request,
  );
  if (!response.data) throw new Error("Failed to clone system template.");
  return response.data;
}
